import { Box, Stack, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import { matchSorter } from 'match-sorter'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ChipListSingleValue } from 'components/common/chips/ChipList'
import { SearchField } from 'components/common/SearchField'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { SEARCH_INPUT_FIELD_WIDTH } from 'components/configurations/configurations-utils'
import { ActivatePrivateNetworkIntegrationClientButton } from 'components/configurations/private-network-integrations/ActivatePrivateNetworkIntegrationClientButton'
import { DeactivatePrivateNetworkIntegrationButton } from 'components/configurations/private-network-integrations/DeactivatePrivateNetworkIntegrationButton'
import { PrivateNetworkIntegrationsSummary } from 'components/configurations/private-network-integrations/PrivateNetworkIntegrationsSummary'
import useAuth from 'hooks/use-auth'
import {
  SEARCH_PARAMS_KEY,
  useDebouncedSearchParams
} from 'hooks/use-debounced-search-params'
import { type IntegrationClientsResponse } from 'queries/integration-clients/use-get-integration-clients'
import { ClientState } from 'types/api/attacks/client'
import { type PrivateNetworkIntegrationClient } from 'types/api/integration-clients/integration-clients'
import { type Team } from 'types/api/teams'
import {
  integrationClientsIdActivatePut,
  integrationClientsIdDelete
} from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

const gridStyles = getGridStyles()

function getPrivateNetworkIntegrationDisplayStatus(state: ClientState) {
  switch (state) {
    case ClientState.Active:
      return 'Active'
    case ClientState.Revoked:
      return 'Deactivated'
    default:
      return 'Idle'
  }
}

const PrivateNetworkIntegrationsListEmptyState: React.FC<{
  isFiltered: boolean
}> = ({ isFiltered }) => {
  const message = isFiltered
    ? 'No Private Network Integrations found that match the search criteria'
    : 'No Private Network Integrations found'

  return (
    <GridOverlay>
      <Typography>{message}</Typography>
    </GridOverlay>
  )
}

export const PrivateNetworkIntegrationsList: React.FC<{
  integrationClients: IntegrationClientsResponse
  teams: Team[]
}> = ({ integrationClients, teams }) => {
  const { companyPrefs, hasEndpointAccessForAll } = useAuth()
  const { pni_scope: pniScope } = companyPrefs
  const [searchParams] = useSearchParams()
  const searchParamsValue = searchParams.get(SEARCH_PARAMS_KEY) ?? ''
  const [searchValue, setSearchValue] = useDebouncedSearchParams({
    initialSearchValue: searchParamsValue
  })

  const allIntegrationClients = useMemo(() => {
    const { active, idle, inactive } = integrationClients

    return [...active, ...inactive, ...idle]
  }, [integrationClients])

  const filteredRows = useMemo(() => {
    if (!searchParamsValue) {
      return allIntegrationClients
    }

    return matchSorter(allIntegrationClients, searchParamsValue, {
      keys: ['identifier'],
      threshold: matchSorter.rankings.CONTAINS
    })
  }, [allIntegrationClients, searchParamsValue])

  const columns: GridColumns<PrivateNetworkIntegrationClient> = useMemo(() => {
    return [
      {
        ...defaultColumnProps,
        field: 'state',
        headerName: 'Status',
        flex: 0.75,
        maxWidth: 100,
        renderCell: ({ row }) => {
          const isActive = row.state === ClientState.Active
          const status = getPrivateNetworkIntegrationDisplayStatus(row.state)

          return (
            <Typography
              color={isActive ? 'success.main' : 'text.primary.main'}
              variant="inherit"
            >
              {status}
            </Typography>
          )
        }
      },
      {
        ...defaultColumnProps,
        field: 'identifier',
        headerName: 'Identifier',
        maxWidth: 100,
        flex: 1.5,
        renderCell: ({ row }) => (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: row.identifier }}
            variant="inherit"
          >
            {row.identifier}
          </TruncatedTextWithTooltip>
        )
      },
      {
        ...defaultColumnProps,
        field: 'tags',
        headerName: 'Tags',
        flex: 1.75,
        renderCell: ({ row }) => (
          <ChipListSingleValue
            sx={(muiTheme) => ({
              maxWidth: `calc(100% - ${
                // width of overflow Chip:
                muiTheme.spacing(3)
              })`
            })}
            tags={row.tags}
            visibleTagsCount={1}
          />
        )
      },
      {
        ...defaultColumnProps,
        field: 'orgId',
        headerName: 'Owning Team',
        flex: 1.75,
        renderCell: ({ row }) => {
          const teamName = teams.find(
            (team) => team.identifier === row.orgId
          )?.name

          return (
            <TruncatedTextWithTooltip
              tooltipProps={{ title: teamName }}
              variant="inherit"
            >
              {teamName}
            </TruncatedTextWithTooltip>
          )
        }
      },
      {
        ...defaultColumnProps,
        field: 'sharedAcross',
        headerName: 'Shared Across',
        flex: 0.75,
        minWidth: 100,
        renderCell: () => <Typography variant="inherit">{pniScope}</Typography>
      },
      {
        ...defaultColumnProps,
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        renderCell: ({ row }) => {
          if (row.state === ClientState.Revoked) {
            return (
              <ActivatePrivateNetworkIntegrationClientButton
                integrationClient={row}
              />
            )
          }

          return (
            <DeactivatePrivateNetworkIntegrationButton
              integrationClient={row}
            />
          )
        }
      }
    ]
  }, [pniScope, teams])

  return (
    <Stack sx={{ rowGap: 4 }}>
      <Stack direction="row" sx={{ columnGap: 4 }}>
        <SearchField
          data-testid="private-network-integrations-search"
          helperText="Search by identifier"
          InputProps={{ sx: { width: SEARCH_INPUT_FIELD_WIDTH } }}
          label="Search"
          value={searchValue}
          onChange={setSearchValue}
        />
        <PrivateNetworkIntegrationsSummary
          integrationClients={integrationClients}
        />
      </Stack>
      <Box data-testid="private-network-integrations-list">
        <DataGrid
          {...defaultGridProps}
          columns={columns}
          columnVisibilityModel={{
            actions: hasEndpointAccessForAll([
              integrationClientsIdDelete,
              integrationClientsIdActivatePut
            ])
          }}
          components={{
            NoRowsOverlay: () => (
              <PrivateNetworkIntegrationsListEmptyState
                isFiltered={Boolean(searchParamsValue)}
              />
            )
          }}
          getRowId={(value) => value.identifier}
          rows={filteredRows}
          sx={gridStyles}
        />
      </Box>
    </Stack>
  )
}
